import { FACTOR_ENUMS, BUREAU_ENUMS } from 'pages/RedesignDashboard/constants';
import { getDifferenceInMillis } from 'helpers/dateHelper';
import { getCardModule } from 'helpers/offer.helpers';

export const BureauMap = {
  [BUREAU_ENUMS.ALL]: 'all',
  [BUREAU_ENUMS.EQUIFAX]: 'equifax',
  [BUREAU_ENUMS.EXPERIAN]: 'experian',
  [BUREAU_ENUMS.TRANS_UNION]: 'transUnion',
};

const getFactorItem = (factor, refreshDate = new Date()) => {
  const {
    paymentHistory,
    creditUsage,
    creditAge,
    accountMix,
    inquiries,
    sesameGrade,
    score,
  } = factor || {};
  return {
    sesameGrade: {
      qualification: sesameGrade,
      score,
      date: refreshDate,
    },
    data: [
      {
        value: 35,
        displayLabel: 40,
        focus: false,
        id: 1,
        name: FACTOR_ENUMS.PAYMENT_HISTORY,
        qualification: paymentHistory?.grade,
        hasNotifications: paymentHistory?.pimpleCount > 0,
        numberOfNotifications: paymentHistory?.pimpleCount,
        pimpleColor: paymentHistory?.pimpleColor,
      },
      {
        value: 30,
        displayLabel: 34,
        focus: false,
        id: 2,
        name: FACTOR_ENUMS.CREDIT_USAGE,
        qualification: creditUsage?.grade,
        hasNotifications: creditUsage?.pimpleCount > 0,
        numberOfNotifications: creditUsage?.pimpleCount,
        pimpleColor: creditUsage?.pimpleColor,
      },
      {
        value: 15,
        displayLabel: 21,
        focus: false,
        id: 3,
        name: FACTOR_ENUMS.CREDIT_AGE,
        qualification: creditAge?.grade,
        hasNotifications: creditAge?.pimpleCount > 0,
        numberOfNotifications: creditAge?.pimpleCount,
        pimpleColor: creditAge?.pimpleColor,
      },
      {
        value: 10,
        displayLabel: 21,
        focus: false,
        id: 4,
        name: FACTOR_ENUMS.ACCOUNT_MIX,
        qualification: accountMix?.grade,
        hasNotifications: accountMix?.pimpleCount > 0,
        numberOfNotifications: accountMix?.pimpleCount,
        pimpleColor: accountMix?.pimpleColor,
      },
      {
        value: 10,
        displayLabel: 5,
        focus: false,
        id: 5,
        name: FACTOR_ENUMS.CREDIT_INQUIRIES,
        qualification: inquiries?.grade,
        hasNotifications: inquiries?.pimpleCount > 0,
        numberOfNotifications: inquiries?.pimpleCount,
        pimpleColor: inquiries?.pimpleColor,
      },
    ],
  };
};

export const getGradeDialData = profileContainer => {
  const scoreDial = getCardModule(profileContainer, 'ALL_BUREAU_SCORE_DIALS');
  const creditFactor = getCardModule(
    profileContainer,
    'ALL_BUREAU_CREDIT_FACTORS',
  );
  const eventEngagement = getCardModule(
    profileContainer,
    'PROFILE_PAGE_EVENT_ENGAGEMENT',
  );

  const { bureauScores = {}, creditProfileRefreshDate = {} } =
    scoreDial?.additionalData || {};

  const creditScoreData = [
    BUREAU_ENUMS.TRANS_UNION,
    BUREAU_ENUMS.EQUIFAX,
    BUREAU_ENUMS.EXPERIAN,
  ].reduce((creditScoreList, key) => {
    if (BUREAU_ENUMS[key] === BUREAU_ENUMS.TRANS_UNION) {
      (creditScoreList || []).push({
        bureau: BUREAU_ENUMS.TRANS_UNION,
        score: bureauScores?.['transUnion'],
        scoreRating: '',
        scoreDate: creditProfileRefreshDate?.['transUnion'],
        provider: 'TRANS_UNION',
      });
    } else {
      const lowerCaseKey = key.toLocaleLowerCase();
      (creditScoreList || []).push({
        bureau: BUREAU_ENUMS[key],
        score: bureauScores?.[lowerCaseKey],
        scoreRating: '',
        scoreDate: creditProfileRefreshDate?.[lowerCaseKey],
        provider: 'TRANS_UNION',
      });
    }
    return creditScoreList;
  }, []);

  const { bureauFactorDetails: { all, equifax, experian, transUnion } = {} } =
    creditFactor?.additionalData || {};

  const { eventInfo = {} } = eventEngagement?.additionalData || {};

  const refreshDateForEquifax = creditProfileRefreshDate?.['equifax'];
  const refreshDateForExperian = creditProfileRefreshDate?.['experian'];

  const olderDate =
    getDifferenceInMillis(refreshDateForEquifax, refreshDateForExperian) > 0
      ? refreshDateForExperian
      : refreshDateForEquifax;
  const creditFactorData = {
    transUnion: getFactorItem({
      ...transUnion,
      score: bureauScores?.['transUnion'],
    }),
    equifax: getFactorItem(
      { ...equifax, score: bureauScores?.['equifax'] },
      refreshDateForEquifax,
    ),
    experian: getFactorItem(
      { ...experian, score: bureauScores?.['experian'] },
      refreshDateForExperian,
    ),
    all: getFactorItem(
      { ...all, score: bureauScores?.['transUnion'] },
      olderDate,
    ),
  };

  return { creditScoreData, creditFactorData, eventInfo };
};

const getPotentialFactor = potentialProfileData => {
  const { accountMix, creditAge, creditUsage, inquiries, paymentHistory } =
    potentialProfileData?.additionalData || {};

  const getActionsCount = factorData => {
    const { actions = [] } = factorData || {};
    const filteredActions = actions.filter(action =>
      action?.displayArea?.includes('POTENTIAL_TAB'),
    );

    return filteredActions?.length || 0;
  };

  const accountMixActionCount = getActionsCount(accountMix);
  const creditAgeActionCount = getActionsCount(creditAge);
  const creditUsageActionCount = getActionsCount(creditUsage);
  const inquiriesActionCount = getActionsCount(inquiries);
  const paymentHistoryActionCount = getActionsCount(paymentHistory);

  return {
    sesameGrade: {
      qualification:
        accountMixActionCount +
        creditAgeActionCount +
        creditUsageActionCount +
        inquiriesActionCount +
        paymentHistoryActionCount,
    },
    data: [
      {
        value: 35,
        focus: false,
        id: 1,
        name: FACTOR_ENUMS.PAYMENT_HISTORY,
        qualification: paymentHistory?.grade,
        hasNotifications: paymentHistoryActionCount > 0,
        numberOfNotifications: paymentHistoryActionCount,
        pimpleColor: paymentHistory?.pimpleColor,
      },
      {
        value: 30,
        focus: false,
        id: 2,
        name: FACTOR_ENUMS.CREDIT_USAGE,
        qualification: creditUsage?.grade,
        hasNotifications: creditUsageActionCount > 0,
        numberOfNotifications: creditUsageActionCount,
        pimpleColor: creditUsage?.pimpleColor,
      },
      {
        value: 15,
        focus: false,
        id: 3,
        name: FACTOR_ENUMS.CREDIT_AGE,
        qualification: creditAge?.grade,
        hasNotifications: creditAgeActionCount > 0,
        numberOfNotifications: creditAgeActionCount,
        pimpleColor: creditAge?.pimpleColor,
      },
      {
        value: 10,
        focus: false,
        id: 4,
        name: FACTOR_ENUMS.ACCOUNT_MIX,
        qualification: accountMix?.grade,
        hasNotifications: accountMixActionCount > 0,
        numberOfNotifications: accountMixActionCount,
        pimpleColor: accountMix?.pimpleColor,
      },
      {
        value: 10,
        focus: false,
        id: 5,
        name: FACTOR_ENUMS.CREDIT_INQUIRIES,
        qualification: inquiries?.grade,
        hasNotifications: inquiriesActionCount > 0,
        numberOfNotifications: inquiriesActionCount,
        pimpleColor: inquiries?.pimpleColor,
      },
    ],
  };
};

export const getPotentialGradeDialData = profileContainer => {
  const potentialProfileData = getCardModule(
    profileContainer,
    'POTENTIAL_ALL_BUREAU_SCORES_CREDIT_FACTORS',
  );
  const { nextRefreshDate, scoreImprovement, scores } =
    potentialProfileData?.additionalData || {};

  const creditScoreData = [
    BUREAU_ENUMS.TRANS_UNION,
    BUREAU_ENUMS.EQUIFAX,
    BUREAU_ENUMS.EXPERIAN,
  ].reduce((creditScoreList, key) => {
    const lowerCaseKey = BureauMap[BUREAU_ENUMS[key]];
    const score = scores?.[lowerCaseKey];
    const scoreChange = scoreImprovement?.[lowerCaseKey];

    (creditScoreList || []).push({
      bureau: BUREAU_ENUMS[key],
      score,
      scoreChange,
      scoreRating: '',
      scoreDate: nextRefreshDate?.[lowerCaseKey],
      provider: 'TRANS_UNION',
    });

    return creditScoreList;
  }, []);
  const creditFactorData = getPotentialFactor(potentialProfileData);

  return { creditScoreData, creditFactorData };
};

export const hasPotentialGradeDialData = scoreData => {
  const scoreChange = scoreData?.find(
    item => item.bureau === BUREAU_ENUMS.TRANS_UNION,
  )?.scoreChange;

  return scoreChange && scoreChange > 0 ? true : false;
};

export const isFreeUserWith1B = gradeDialData => {
  return (
    gradeDialData?.creditFactorData[[BureauMap[BUREAU_ENUMS.ALL]]]?.sesameGrade
      .qualification === undefined
  );
};

export const getTUCreditScore = scoreData => {
  return scoreData?.find(item => item.bureau === BUREAU_ENUMS.TRANS_UNION)
    ?.score;
};

export const getImprovementScore = impScoreData => {
  const potentialCreditData = getCardModule(
    impScoreData,
    'POTENTIAL_ALL_BUREAU_SCORES_CREDIT_FACTORS',
  );

  return potentialCreditData?.additionalData?.scoreImprovement?.transUnion;
};
